import React, { useState, useMemo, useEffect } from 'react';
import { APIProvider, Map, InfoWindow, MapControl, ControlPosition } from '@vis.gl/react-google-maps';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { CircularProgress, Stack, Switch, Typography } from '@mui/material';
import { addOpacityToHexColor, capitalize } from '../../common/utils/common.util';
import { getClusterAlgorithm, mapControlStyles, offsetDuplicateCoordinates } from './map.util';
import { areaColors, getColorByArea } from '../project/project.util';
import { Launch } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { ROUTES } from '../../common/constants/urls';
import MapAreaLegend from './MapAreaLegend';
import { INFO_WINDOW_TIMEOUT, US_CENTER } from '../../common/constants/maps';
import MapProjectsList from './MapProjectsList';
import { compareArrays } from '../report/report.util';

interface MapListOptions {
  hideList?: boolean;
  checkboxSelection?: boolean;
  onProjectsSelect?: (selectedProjectsIds: string[]) => void;
  disabledProjectsIds?: string[];
  hoveredProjectId?: string;
  loading?: boolean;
}

interface MapWithPinsProps {
  projects: any[] | undefined;
  loading: boolean;
  listOptions?: MapListOptions;
  zoomLevel?: number;
}

const containerStyle = { width: '100%', height: '100%' };

const ProjectListMap: React.FC<MapWithPinsProps> = ({ projects, loading, zoomLevel, listOptions = { hideList: false } }) => {
  const [hoveredProject, setHoveredProject] = useState<any | null>(null);
  const [clusterer, setClusterer] = useState<MarkerClusterer | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const [mapMarkers, setMapMarkers] = useState<google.maps.marker.AdvancedMarkerElement[]>([]);
  const [isClusteringEnabled, setIsClusteringEnabled] = useState(true);
  const [isInfoWindowHovered, setIsInfoWindowHovered] = useState(false); // Track InfoWindow hover state
  const [visibleProjects, setVisibleProjects] = useState<any[]>([]);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const handleMouseOverMarker = (project: any) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setHoveredProject(project);
  };

  const handleMouseOutMarker = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      if (!isInfoWindowHovered) {
        setHoveredProject(null);
      }
    }, INFO_WINDOW_TIMEOUT);
  };

  const handleMouseOverInfoWindow = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsInfoWindowHovered(true);
  };

  const handleMouseOutInfoWindow = () => {
    timeoutRef.current = setTimeout(() => {
      setIsInfoWindowHovered(false);
      setHoveredProject(null);
    }, INFO_WINDOW_TIMEOUT);
  };

  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
  const mapId = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || '';

  const validCoordinates = useMemo(() => {
    return offsetDuplicateCoordinates(
      (projects || [])
        .filter(
          project =>
            project?.Latitude !== null &&
            project?.Latitude !== undefined &&
            project?.Longitude !== null &&
            project?.Longitude !== undefined,
        )
        .map(project => ({
          lat: Number(project.Latitude),
          lng: Number(project.Longitude),
          project,
        })),
    );
  }, [projects]);

  useEffect(() => {
    setVisibleProjects(validCoordinates.map(coord => coord.project));
  }, [validCoordinates]);

  useEffect(() => {
    if (!map || !isMapReady || clusterer) return;

    const overlay = new google.maps.OverlayView();
    overlay.onAdd = () => {
      const projection = overlay.getProjection();
      if (!projection) {
        console.warn('Projection is not ready yet.');
        return;
      }
      const newClusterer = getNewClusterer(map, validCoordinates);
      setClusterer(newClusterer);
    };
    overlay.setMap(map);
  }, [map, isMapReady, clusterer]);

  useEffect(() => {
    if (!map || !isMapReady) return;
    if (clusterer) {
      clusterer.clearMarkers();
      clusterer.setMap(null);
    }
    const newClusterer = getNewClusterer(map, validCoordinates);
    setClusterer(newClusterer);
  }, [validCoordinates?.length]);

  useEffect(() => {
    const updateMarkers = async () => {
      if (!clusterer || !map || !isMapReady || loading) {
        console.warn('Skipping updateMarkers: Dependencies are not ready.');
        return;
      }

      clusterer.clearMarkers();
      mapMarkers.forEach(marker => (marker.map = null));

      const { AdvancedMarkerElement, PinElement } = (await google.maps.importLibrary('marker')) as {
        AdvancedMarkerElement: typeof google.maps.marker.AdvancedMarkerElement;
        PinElement: typeof google.maps.marker.PinElement;
      };

      const markers = validCoordinates.map(coord => {
        const area = coord.project.CustomArea;
        const color = getColorByArea(area);

        const pin = new PinElement({
          background: addOpacityToHexColor(color, 80),
          borderColor: '#FFFFFF',
          glyphColor: color,
          scale: 1,
        });

        const marker = new AdvancedMarkerElement({
          position: { lat: coord.lat, lng: coord.lng },
          content: pin.element,
          gmpClickable: true,
        });

        // Attach hover listeners to the marker's root DOM element (div wrapper)
        marker.element?.addEventListener('mouseenter', () => handleMouseOverMarker(coord));
        marker.element?.addEventListener('mouseleave', handleMouseOutMarker);

        return marker;
      });
      setMapMarkers(markers);

      if (isClusteringEnabled) {
        clusterer.addMarkers(markers);
      } else {
        markers.forEach(marker => (marker.map = map));
      }

      if (validCoordinates.length > 0) {
        if (zoomLevel !== undefined) {
          // If a zoomLevel is provided, set the zoom and center to the first valid coordinate
          map.setZoom(zoomLevel);
          map.setCenter(US_CENTER);
        } else {
          const bounds = new google.maps.LatLngBounds();
          validCoordinates.forEach(coord => bounds.extend({ lat: coord.lat, lng: coord.lng }));
          map.fitBounds(bounds);
        }
      }
    };

    updateMarkers();
  }, [clusterer, validCoordinates, map, isMapReady, loading, isClusteringEnabled]);

  const handleBoundsChange = () => {
    if (!map) return;
    const bounds = map.getBounds();
    if (!bounds) return;
    const filteredProjects = validCoordinates
      .filter(coord => bounds.contains({ lat: coord.lat, lng: coord.lng }))
      .map(coord => coord.project);

    if (compareArrays(filteredProjects, visibleProjects)) return;

    setVisibleProjects(filteredProjects);
  };

  const setHoveredProjectId = (id: string | null) => {
    if (id === null) setHoveredProject(null);
    setHoveredProject(validCoordinates.find(coord => coord?.project?.Id === id));
  };

  return (
    <div style={wrapperStyles}>
      {!listOptions?.hideList && (
        <MapProjectsList
          projects={visibleProjects}
          checkboxSelection={listOptions?.checkboxSelection}
          onProjectsSelect={listOptions?.onProjectsSelect}
          disabledProjectsIds={listOptions?.disabledProjectsIds}
          hoveredProjectId={hoveredProject?.project?.Id}
          setHoveredProjectId={setHoveredProjectId}
          loading={loading}
        />
      )}
      <div style={{ flex: 1 }}>
        <APIProvider apiKey={googleMapsApiKey}>
          <Map
            mapId={mapId}
            style={containerStyle}
            defaultCenter={US_CENTER}
            defaultZoom={zoomLevel || 3}
            onIdle={event => {
              const mapInstance = event.map;
              if (mapInstance instanceof google.maps.Map) {
                setMap(mapInstance);
                mapInstance.setOptions({ mapTypeId: 'roadmap' });
              }
              handleBoundsChange();
            }}
            onTilesLoaded={() => setIsMapReady(true)}
          >
            {loading && (
              <div style={loadingOverlayStyles}>
                <div style={{ textAlign: 'center' }}>
                  <CircularProgress />
                </div>
              </div>
            )}
            <MapControl position={ControlPosition.TOP_CENTER}>
              <MapAreaLegend
                data={[
                  ...Object.entries(areaColors).map(([area, color]) => ({ area: area.toUpperCase(), color })),
                  { area: 'DEFAULT', color: 'red' },
                ]}
              />
            </MapControl>
            <MapControl position={ControlPosition.TOP_RIGHT}>
              <Stack direction='row' alignItems='center' pr={2} pl={0} boxShadow={1} sx={mapControlStyles}>
                <Switch checked={isClusteringEnabled} onChange={() => setIsClusteringEnabled(val => !val)} />
                <Typography variant='body2' fontWeight={'bold'}>
                  Clusters
                </Typography>
              </Stack>
            </MapControl>
            {hoveredProject && (
              <InfoWindow
                style={{ minWidth: '100px' }}
                position={{ lat: hoveredProject.lat, lng: hoveredProject.lng }}
                pixelOffset={[0, -30]}
                onCloseClick={() => setHoveredProject(null)}
              >
                <div
                  style={{ minWidth: '100px', textAlign: 'center' }}
                  onMouseOver={handleMouseOverInfoWindow}
                  onMouseOut={handleMouseOutInfoWindow}
                >
                  <Link
                    to={`${ROUTES.PROJECTS}/${hoveredProject.project.Id}`}
                    target='_blank'
                    style={{ display: 'flex', alignItems: 'center', width: '100%', color: 'unset', justifyContent: 'center' }}
                  >
                    <p style={{ margin: '0', fontWeight: 'bold' }}>
                      {hoveredProject.project.ProjectNumber} - {hoveredProject.project.ProjectTitle}
                    </p>
                    <Launch sx={{ fontSize: '16px', ml: '5px' }} color='primary' />
                  </Link>
                  <p style={{ marginBottom: 0 }}>{capitalize(hoveredProject.project?.City)}</p>
                  <p style={{ marginBottom: 0 }}>Project Type: {capitalize(hoveredProject.project?.ProjectType || '')}</p>
                  <p>Observer: {capitalize(hoveredProject.project?.Observer || '')}</p>
                </div>
              </InfoWindow>
            )}
          </Map>
        </APIProvider>
      </div>
    </div>
  );
};

const getNewClusterer = (map: google.maps.Map, validCoordinates: any[]) => {
  const algorithm = getClusterAlgorithm();

  const newClusterer = new MarkerClusterer({
    map,
    algorithm,
    renderer: {
      render: ({ count, position }) => {
        const marker = new google.maps.marker.AdvancedMarkerElement({
          position,
          content: createClusterBubble(count, validCoordinates.length),
        });
        return marker;
      },
    },
  });

  return newClusterer;
};

const createClusterBubble = (count: number, maxCount = 200) => {
  const minCount = 1;
  const minSize = 30;
  const maxSize = 100;

  const size = minSize + ((Math.min(count, maxCount) - minCount) / (maxCount - minCount)) * (maxSize - minSize);

  const div = document.createElement('div');
  div.style.background = 'rgba(63, 81, 181, 0.5)';
  div.style.color = '#ffffff';
  div.style.border = '2px solid rgba(63, 81, 181)';
  div.style.borderRadius = '50%';
  div.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.3)';
  div.style.width = `${size}px`;
  div.style.height = `${size}px`;
  div.style.display = 'flex';
  div.style.justifyContent = 'center';
  div.style.alignItems = 'center';
  div.style.fontSize = `${Math.min(12 + count * 0.2, 24)}px`;
  div.style.zIndex = `1000`;
  div.textContent = `${count}`;
  return div;
};

const wrapperStyles = {
  display: 'flex',
  flex: 1,
  height: '100%',
  maxHeight: '100%',
  borderRadius: 5,
  overflow: 'hidden',
};

const loadingOverlayStyles = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  zIndex: 10,
} as const;

export default React.memo(ProjectListMap);
